import React, { useState, useEffect } from 'react';
import { tasksAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Plus, Edit, Trash2, Target, Gift, History } from 'lucide-react';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const TasksManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [claimHistory, setClaimHistory] = useState([]);
  const [totalClaims, setTotalClaims] = useState(0); // NOVO - total de registros
  const [loadingMore, setLoadingMore] = useState(false); // NOVO - carregando mais
  const [hasMore, setHasMore] = useState(false); // NOVO - tem mais para carregar
  const [sortBy, setSortBy] = useState('date_desc'); // NOVO - ordenação
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    kako_id: '',
    start_date: '',
    end_date: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_coins: '',  // Mudado de target_amount
    reward_coins: '',
    active: true
  });

  useEffect(() => {
    loadTasks();
    loadClaimHistory();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await tasksAPI.listAll();
      setTasks(response.data);
    } catch (error) {
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const loadClaimHistory = async (append = false) => {
    try {
      const params = {
        skip: append ? claimHistory.length : 0,
        limit: 100
      };
      if (historyFilters.kako_id) params.kako_id = historyFilters.kako_id;
      if (historyFilters.start_date) params.start_date = historyFilters.start_date;
      if (historyFilters.end_date) params.end_date = historyFilters.end_date;
      
      const response = await tasksAPI.getClaimHistory(params);
      const data = response.data;
      
      // Se API retornar array direto (formato antigo), compatibilidade
      if (Array.isArray(data)) {
        setClaimHistory(append ? [...claimHistory, ...data] : data);
        setHasMore(false);
        setTotalClaims(data.length);
      } else {
        // Formato novo com paginação
        setClaimHistory(append ? [...claimHistory, ...data.items] : data.items);
        setHasMore(data.has_more || false);
        setTotalClaims(data.total || 0);
      }
      
      if (!append) {
        setSelectedClaims([]); // Limpa seleção apenas quando não é append
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoadingMore(false);
    }
  };
  
  const loadMoreClaims = async () => {
    setLoadingMore(true);
    await loadClaimHistory(true);
  };

  const handleDeleteSelectedClaims = async () => {
    if (selectedClaims.length === 0) {
      toast.error('Selecione pelo menos um resgate');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await tasksAPI.deleteClaimsBulk(selectedClaims);
      toast.success(`${selectedClaims.length} resgate(s) excluído(s)!`);
      setShowDeleteConfirm(false);
      loadClaimHistory();
    } catch (error) {
      toast.error('Erro ao excluir resgates');
      setShowDeleteConfirm(false);
    }
  };

  const toggleClaimSelection = (claimId) => {
    setSelectedClaims(prev => 
      prev.includes(claimId) 
        ? prev.filter(id => id !== claimId)
        : [...prev, claimId]
    );
  };

  const toggleSelectAll = (dayClaims) => {
    const dayIds = dayClaims.map(c => c.claim_id);
    const allSelected = dayIds.every(id => selectedClaims.includes(id));
    
    if (allSelected) {
      setSelectedClaims(prev => prev.filter(id => !dayIds.includes(id)));
    } else {
      setSelectedClaims(prev => [...new Set([...prev, ...dayIds])]);
    }
  };

  const handleApplyFilters = () => {
    loadClaimHistory();
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description,
        target_coins: (task.target_coins || task.target_amount || 0).toString(),
        reward_coins: task.reward_coins.toString(),
        active: task.active
      });
    } else {
      setEditingTask(null);
      setFormData({ title: '', description: '', target_coins: '', reward_coins: '', active: true });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      title: formData.title,
      description: formData.description,
      target_coins: parseInt(formData.target_coins),
      reward_coins: parseInt(formData.reward_coins),
      active: formData.active
    };

    try {
      if (editingTask) {
        await tasksAPI.update(editingTask.id, data);
        toast.success('Tarefa atualizada!');
      } else {
        await tasksAPI.create(data);
        toast.success('Tarefa criada!');
      }
      setDialogOpen(false);
      loadTasks();
      loadClaimHistory();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) return;
    try {
      await tasksAPI.delete(id);
      toast.success('Tarefa excluída!');
      loadTasks();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tasks-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tarefas Diárias</h2>
          <p className="text-gray-600">Gerencie as metas de recarga com bônus</p>
        </div>
        <Button data-testid="add-task-button" onClick={() => handleOpenDialog()} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" />
          Nova Tarefa
        </Button>
      </div>

      <Tabs defaultValue="tasks" className="w-full">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="tasks">
              <Target className="w-4 h-4 mr-2" />
              Gerenciar Tarefas
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-2" />
              Histórico de Resgates
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="tasks" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2">
        {tasks.map((task) => (
          <Card key={task.id} data-testid={`task-card-${task.id}`} className={!task.active ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">{task.title}</CardTitle>
                </div>
                <Badge variant={task.active ? 'default' : 'secondary'}>
                  {task.active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{task.description}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Meta de Recarga</p>
                  <p className="text-xl font-bold text-orange-600">{(task.target_coins || task.target_amount || 0).toLocaleString()} 🪙</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bônus</p>
                  <p className="text-xl font-bold text-purple-600">{task.reward_coins} moedas</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid={`edit-task-${task.id}`} onClick={() => handleOpenDialog(task)} variant="outline" className="flex-1">
                  <Edit className="mr-2 h-4 w-4" />
                  Editar
                </Button>
                <Button data-testid={`delete-task-${task.id}`} onClick={() => handleDelete(task.id)} variant="destructive" className="flex-1">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card data-testid="claim-history-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-purple-600" />
                  Histórico de Resgates
                </CardTitle>
                <Badge variant="secondary">{claimHistory.length} resgates</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtros e Ordenação */}
              <div className="grid gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label className="text-sm">KAKO ID</Label>
                  <Input
                    data-testid="filter-kako-id"
                    placeholder="Filtrar por KAKO ID"
                    value={historyFilters.kako_id}
                    onChange={(e) => setHistoryFilters({ ...historyFilters, kako_id: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Início</Label>
                  <Input
                    data-testid="filter-start-date"
                    type="date"
                    value={historyFilters.start_date}
                    onChange={(e) => setHistoryFilters({ ...historyFilters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Data Fim</Label>
                  <Input
                    data-testid="filter-end-date"
                    type="date"
                    value={historyFilters.end_date}
                    onChange={(e) => setHistoryFilters({ ...historyFilters, end_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Ordenar por</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date_desc">Data (Recente)</SelectItem>
                      <SelectItem value="date_asc">Data (Antigo)</SelectItem>
                      <SelectItem value="claimed_desc">Resgate (Recente)</SelectItem>
                      <SelectItem value="claimed_asc">Resgate (Antigo)</SelectItem>
                      <SelectItem value="coins_desc">Moedas (Maior)</SelectItem>
                      <SelectItem value="coins_asc">Moedas (Menor)</SelectItem>
                      <SelectItem value="reward_desc">Recompensa (Maior)</SelectItem>
                      <SelectItem value="reward_asc">Recompensa (Menor)</SelectItem>
                      <SelectItem value="kako_id_asc">KAKO ID (A-Z)</SelectItem>
                      <SelectItem value="kako_id_desc">KAKO ID (Z-A)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">&nbsp;</Label>
                  <Button 
                    data-testid="apply-history-filters"
                    onClick={handleApplyFilters} 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              {/* Botão de Delete Selecionados */}
              {selectedClaims.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded">
                  <span className="text-sm font-medium">
                    {selectedClaims.length} resgate(s) selecionado(s)
                  </span>
                  <Button
                    onClick={handleDeleteSelectedClaims}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir Selecionados
                  </Button>
                </div>
              )}

              {/* Lista de Resgates */}
              <div className="space-y-3">
                {claimHistory.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhum resgate realizado ainda</p>
                ) : (
                  // Ordena antes de agrupar
                  (() => {
                    const sortedHistory = [...claimHistory].sort((a, b) => {
                      switch (sortBy) {
                        case 'date_desc':
                          return b.date.localeCompare(a.date);
                        case 'date_asc':
                          return a.date.localeCompare(b.date);
                        case 'claimed_desc':
                          return new Date(b.reward_claimed_at) - new Date(a.reward_claimed_at);
                        case 'claimed_asc':
                          return new Date(a.reward_claimed_at) - new Date(b.reward_claimed_at);
                        case 'coins_desc':
                          return (b.current_amount || 0) - (a.current_amount || 0);
                        case 'coins_asc':
                          return (a.current_amount || 0) - (b.current_amount || 0);
                        case 'reward_desc':
                          return (b.reward_coins || 0) - (a.reward_coins || 0);
                        case 'reward_asc':
                          return (a.reward_coins || 0) - (b.reward_coins || 0);
                        case 'kako_id_asc':
                          return (a.kako_id || '').localeCompare(b.kako_id || '');
                        case 'kako_id_desc':
                          return (b.kako_id || '').localeCompare(a.kako_id || '');
                        default:
                          return 0;
                      }
                    });
                    
                    // Agrupa por data após ordenar
                    return Object.entries(
                      sortedHistory.reduce((groups, claim) => {
                        const date = claim.date;
                        if (!groups[date]) groups[date] = [];
                        groups[date].push(claim);
                        return groups;
                      }, {})
                    ).map(([date, dayClaims]) => (
                      <div key={date} className="border rounded-lg p-3 bg-purple-50 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={dayClaims.every(c => selectedClaims.includes(c.claim_id))}
                            onCheckedChange={() => toggleSelectAll(dayClaims)}
                          />
                          <h4 className="font-bold text-sm text-purple-600">
                            📅 {date.split('-').reverse().join('/')}
                          </h4>
                        </div>
                        <Badge className="bg-purple-600 text-white">
                          Total do Dia: {dayClaims[0]?.daily_total_coins || 0} 🪙
                        </Badge>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2">
                                <Checkbox
                                  checked={dayClaims.every(c => selectedClaims.includes(c.claim_id))}
                                  onCheckedChange={(checked) => {
                                    dayClaims.forEach(c => {
                                      if (checked && !selectedClaims.includes(c.claim_id)) {
                                        setSelectedClaims([...selectedClaims, c.claim_id]);
                                      } else if (!checked) {
                                        setSelectedClaims(selectedClaims.filter(id => id !== c.claim_id));
                                      }
                                    });
                                  }}
                                />
                              </th>
                              <th className="text-left p-2">Tarefa</th>
                              <th className="text-left p-2">Usuário</th>
                              <th className="text-left p-2">Moedas Atingidas</th>
                              <th className="text-left p-2">Bônus</th>
                              <th className="text-left p-2">Resgatado em</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dayClaims.map((claim) => (
                              <tr key={claim.claim_id} className="border-b hover:bg-gray-50" data-testid={`claim-${claim.claim_id}`}>
                                <td className="p-2">
                                  <Checkbox
                                    checked={selectedClaims.includes(claim.claim_id)}
                                    onCheckedChange={() => toggleClaimSelection(claim.claim_id)}
                                  />
                                </td>
                                <td className="p-2 font-medium">{claim.task_title}</td>
                                <td className="p-2">
                                  <div className="text-xs">
                                    <div className="font-medium">{claim.kako_nickname}</div>
                                    <div className="text-gray-500">ID: {claim.kako_id}</div>
                                  </div>
                                </td>
                                <td className="p-2">{(claim.current_amount || 0).toLocaleString()} 🪙</td>
                                <td className="p-2">
                                  <Badge className="bg-purple-600">
                                    {claim.reward_coins} 🎁
                                  </Badge>
                                </td>
                                <td className="p-2 text-xs">
                                  {claim.reward_claimed_at ? 
                                    new Date(claim.reward_claimed_at).toLocaleString('pt-BR') : 
                                    '-'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                  })() // Fecha a função arrow
                )}
              </div>
              
              {/* Botão Carregar Mais */}
              {hasMore && (
                <div className="text-center mt-4">
                  <Button
                    onClick={loadMoreClaims}
                    disabled={loadingMore}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    {loadingMore ? 'Carregando...' : `Carregar Mais (${totalClaims - claimHistory.length} restantes)`}
                  </Button>
                </div>
              )}
              
              {/* Contador */}
              {claimHistory.length > 0 && (
                <p className="text-xs text-center text-gray-500 mt-4">
                  Mostrando {claimHistory.length} de {totalClaims} resgates
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                data-testid="task-title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                data-testid="task-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta em Moedas (🪙)</Label>
                <Input
                  data-testid="task-target-input"
                  type="number"
                  placeholder="Ex: 100000"
                  value={formData.target_coins}
                  onChange={(e) => setFormData({ ...formData, target_coins: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Moedas de Bônus</Label>
                <Input
                  data-testid="task-reward-input"
                  type="number"
                  value={formData.reward_coins}
                  onChange={(e) => setFormData({ ...formData, reward_coins: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                data-testid="task-active-switch"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label>Tarefa ativa</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button data-testid="save-task-button" type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingTask ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir <strong>{selectedClaims.length} resgate(s)</strong> selecionado(s)?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação irá reverter os resgates de bônus.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksManagement;